import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Building2, Search, MapPin, Loader2, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';

export default function AdminProperties() {
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadProperties();
  }, [page, statusFilter]);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (statusFilter) params.append('status', statusFilter);
      
      const { data } = await api.get(`/admin/properties?${params}`);
      setProperties(data.properties || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'draft': 'bg-slate-100 text-slate-800',
      'listed': 'bg-blue-100 text-blue-800',
      'let': 'bg-green-100 text-green-800',
      'unlisted': 'bg-yellow-100 text-yellow-800',
      'deleted': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const filteredProperties = properties.filter(p => 
    !search || 
    p.address_line1?.toLowerCase().includes(search.toLowerCase()) ||
    p.city?.toLowerCase().includes(search.toLowerCase()) ||
    p.postcode?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(total / 20);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Property Management</h1>
            <p className="text-slate-600">{total} properties on platform</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by address, city, postcode..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">All Statuses</option>
                <option value="listed">Listed</option>
                <option value="let">Let</option>
                <option value="draft">Draft</option>
                <option value="unlisted">Unlisted</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Properties Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">Property</th>
                      <th className="text-left p-4 font-medium text-slate-600">Type</th>
                      <th className="text-left p-4 font-medium text-slate-600">Beds</th>
                      <th className="text-left p-4 font-medium text-slate-600">Rent</th>
                      <th className="text-left p-4 font-medium text-slate-600">Status</th>
                      <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredProperties.map((property) => (
                      <tr key={property.id} className="hover:bg-slate-50">
                        <td className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-16 h-12 bg-slate-100 rounded flex-shrink-0 overflow-hidden">
                              {property.photos?.[0] ? (
                                <img src={property.photos[0]} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Building2 className="w-6 h-6 text-slate-400" />
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{property.address_line1}</p>
                              <p className="text-sm text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {property.city}, {property.postcode}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-slate-600 capitalize">
                          {property.property_type?.replace('_', ' ') || '-'}
                        </td>
                        <td className="p-4 text-slate-600">
                          {property.bedrooms || '-'}
                        </td>
                        <td className="p-4 font-medium text-slate-900">
                          £{property.rent_amount?.toLocaleString() || '-'}/pcm
                        </td>
                        <td className="p-4">
                          <Badge className={getStatusBadge(property.status)}>
                            {property.status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <Link to={`/properties/${property.id}`}>
                              <Button size="sm" variant="outline">
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
